<template>
    <div class="joy-mt-4 notes__item">
        <VJoyWrapper justify="space-between" no-margin>
            <header>
                <span class="joy-font-primary-350 joy-mr-4">{{ note.author.name }}</span>
                <span class="joy-font-primary-300 joy-color-neutral-40">{{ formattedUpdatedDate }}</span>
            </header>
            <NoteActions @delete="handleDelete(note.id)" @edit="editMode = true" />
        </VJoyWrapper>
        <p v-if="!editMode" class="joy-mt-2 content">
            {{ note.content }}
        </p>
        <EditNote v-else :content="note.content" @edit="handleEdit(note, $event)" @cancel="editMode = false" />
    </div>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {intlFormat} from 'date-fns';
    import {useLocale} from '#imports';
    import {VJoyWrapper} from '@maltjoy/core-vue';
    import type {ProfileNoteResource} from '~/components/admin-section-tabs/notes/types/ProfileNoteResource';
    import EditNote from './EditNote.vue';
    import NoteActions from './NoteActions.vue';

    const props = defineProps<{
        note: ProfileNoteResource;
    }>();

    const emits = defineEmits<{
        (e: 'delete', freelancerNoteId: string): void;
        (e: 'edit', value: {note: ProfileNoteResource; content: string}): void;
    }>();

    const {locale} = useLocale();
    const editMode = ref(false);

    const formattedUpdatedDate = computed((): string => {
        if (props.note.editedOn) {
            return intlFormat(
                props.note.editedOn,
                {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                },
                {locale: locale.value},
            );
        }
        return intlFormat(
            props.note.createdOn,
            {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
            },
            {locale: locale.value},
        );
    });

    function handleEdit(note: ProfileNoteResource, content: string) {
        emits('edit', {note, content});
        editMode.value = false;
    }

    function handleDelete(freelancerNoteId: string) {
        emits('delete', freelancerNoteId);
    }
</script>

<style scoped lang="scss">
    .notes {
        &__item {
            background-color: var(--joy-color-neutral-20);
            border-radius: var(--joy-core-radius-3);
            padding: var(--joy-core-spacing-4);
            width: 100%;
            .content {
                white-space: pre-line;
                word-break: normal;
                overflow-wrap: anywhere;
                text-align: left;
            }
        }
    }
</style>
